import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { fetchWorkflowArtifacts } from '@/services/logicAppsApi';
import { getDraftWorkflow } from '@/services/draftWorkflowApi';
import { WorkflowDefinitionSchema } from '@/types/workflowDefinition';
import type { WorkflowDefinition } from '@/types/workflowDefinition';

/**
 * Same parse pattern as WorkflowDesignerPage.parseWorkflow():
 * Zod parse with manual fallback for API data Zod can't handle.
 */
const parseWorkflow = (workflowJson: unknown): WorkflowDefinition | undefined => {
  if (!workflowJson) return undefined;
  const raw = workflowJson as Record<string, unknown>;

  // Ensure wrapper structure: { definition: {...}, kind?: "..." }
  const workflowData = raw?.definition ? raw : { definition: raw };

  const result = WorkflowDefinitionSchema.safeParse(workflowData);
  if (result.success) return result.data;

  // Fallback: Zod may reject valid API data (inputs: null, novel action types).
  // Build WorkflowDefinition manually — this is read-only preview.
  const def = workflowData.definition as Record<string, unknown> | undefined;
  if (def) {
    return {
      definition: {
        triggers: (def.triggers ?? {}) as WorkflowDefinition['definition']['triggers'],
        actions: (def.actions ?? {}) as WorkflowDefinition['definition']['actions'],
        contentVersion: (def.contentVersion as string) ?? '1.0.0.0',
      },
      kind: (workflowData.kind as string) ?? undefined,
    } as WorkflowDefinition;
  }
  return undefined;
};

/**
 * Lazily fetches and caches a sub-workflow's definition by workflow name.
 *
 * Uses the SAME two-step loading pattern as WorkflowDesignerPage:
 *   1. fetchWorkflowArtifacts (ARM $expand) — gets production workflow.json
 *   2. getDraftWorkflow (VFS fallback) — gets workflow-draft.json when $expand doesn't return it
 *   3. Prefer draft if it has content, otherwise fall back to production
 */
export const useSubWorkflowMetadata = (
  workflowName: string | undefined
): {
  definition: WorkflowDefinition | undefined;
  stepCount: number;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
} => {
  const { getOttoToken, isAuthenticated } = useAuth();
  const { selectedApp, selectedProject } = useAppStore();

  const canFetch = !!(isAuthenticated && selectedProject && selectedApp && workflowName);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['subWorkflowDefinition', selectedProject?.toLowerCase(), selectedApp?.name?.toLowerCase(), workflowName],
    queryFn: async () => {
      if (!selectedProject || !selectedApp || !workflowName) return null;

      const fetchOpts = {
        workflowName,
        projectName: selectedProject ?? '',
        appName: selectedApp?.name ?? '',
        getToken: getOttoToken,
      };

      // Step 1: ARM API with $expand (same as WorkflowDesignerPage line 129)
      const { files } = await fetchWorkflowArtifacts({ ...fetchOpts, includeDraft: true });

      const prodWorkflow = parseWorkflow(files?.['workflow.json']);
      let draftWorkflow = parseWorkflow(files?.['workflow-draft.json']);

      // Step 2: VFS fallback for draft (same as WorkflowDesignerPage line 179)
      // ARM $expand often doesn't return workflow-draft.json — fetch it separately
      if (!draftWorkflow) {
        try {
          const draftResult = await getDraftWorkflow(fetchOpts);
          if (draftResult.workflow) {
            draftWorkflow = parseWorkflow(draftResult.workflow);
          }
        } catch {
          // Draft doesn't exist — fine, use production
        }
      }

      const draftActions = draftWorkflow?.definition?.actions ? Object.keys(draftWorkflow.definition.actions).length : 0;

      // Prefer draft if it has content, otherwise production (same as WorkflowDesignerPage line 297)
      return draftActions > 0 ? draftWorkflow : prodWorkflow;
    },
    enabled: canFetch,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const stepCount = useMemo(() => {
    if (!data?.definition?.actions) return 0;
    return Object.keys(data.definition.actions).length;
  }, [data]);

  return {
    definition: data ?? undefined,
    stepCount,
    isLoading,
    isError,
    error: error as Error | null,
  };
};
